import { useEffect, useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ExpandableRow } from "@/components/ui/responsive-table";
import { Switch } from "@/components/ui/switch";
import { useAdminContext } from "@/lib/admin-context";
import { getActiveLangs } from "@/lib/i18n/get-active-languages";

/**
 * Admin → Pricing plans.
 *
 * V1.1 P2 — D21 STRICTER 1:1 port of legacy `src/app/admin/pricing/pricing-client.tsx`
 * (~1397 LOC). Replaces V1.0+ extension LEAN port with full editor surface:
 *   - Per-language tabs + name/description editor with inline AI translate per field
 *   - Volume discount tiers editor (provider plans, conditional on paid)
 *   - Features inline editor (drag up/down + per-language inline edit + AI translate per feature + status select)
 *   - Bulk AI translate button (translate name + description + features at once)
 *   - All adapter conventions (apiFetch / Lingui / TanStack Router)
 *
 * Endpoints (all live, verified pre-port):
 *   - GET /api/admin/pricing → PlanData[]
 *   - PUT /api/admin/pricing → updated plan (full body, includes features array + volumeDiscountTiers)
 *   - POST /api/admin/translate (entity-aware bulk) — used for bulk translate + per-field translate
 *   - POST /api/admin/translate-text (raw text) — used for per-feature inline AI translate
 *
 * V1.1 follow-up declared upfront: dbTarget toggle for inverted cross-env copy direction
 * — N/A for pricing (no copy buttons in legacy pricing-client). Listed for consistency
 * with V1.1 P1 specialties handoff.
 */

type LocalizedString = Record<string, string>;

interface VolumeTier {
  minProviders: number;
  maxProviders: number;
  pricePerProvider: number;
  discountPct: number;
}

interface FeatureItem {
  name: string | LocalizedString;
  status: "active" | "new" | "soon";
  order: number;
}

interface PlanData {
  id: string;
  code: string;
  targetType: string;
  name: Record<string, string>;
  description: Record<string, string>;
  priceMonthly: number;
  priceAnnual: number | null;
  annualDiscountType: string;
  annualDiscountValue: number;
  currency: string;
  bookingsPerMonth: number;
  providersIncluded: number;
  aiNotesPerMonth: number;
  smsPerMonth: number;
  pushPerMonth: number;
  viberPerMonth: number;
  whatsappPerMonth: number;
  extraSmsPrice: number | null;
  byopAllowed: boolean;
  extraBookingsPrice: number | null;
  extraBookingsAmount: number;
  extraProviderPrice: number | null;
  volumeDiscountTiers: VolumeTier[] | null;
  transactionFeePct: number;
  features: FeatureItem[];
  isPopular: boolean;
  isActive: boolean;
  badge: string | null;
  visibility: "active" | "coming_soon" | "draft";
  sortOrder: number;
  version: number;
}

/**
 * Active platform languages — sourced from `getActiveLangs()` at runtime.
 * Falls back to ["ro", "en", "bg"] in dev or when `/api/admin/settings`
 * `active_languages` is empty. Matches legacy `LangInfo` shape minus the
 * `flag` field which legacy fetches from a per-locale lookup; apps/admin
 * inlines flag emoji per locale code (D21 STRICTER preserves intent —
 * functionally equivalent visual indicator).
 */
interface LangInfo {
  code: string;
  flag: string;
}

const LANG_FLAGS: Record<string, string> = {
  ro: "🇷🇴",
  en: "🇬🇧",
  bg: "🇧🇬",
  de: "🇩🇪",
  fr: "🇫🇷",
  hu: "🇭🇺",
  it: "🇮🇹",
  el: "🇬🇷",
  hr: "🇭🇷",
  sr: "🇷🇸",
  cs: "🇨🇿",
  es: "🇪🇸",
  pl: "🇵🇱",
  tr: "🇹🇷",
  uk: "🇺🇦",
  pt: "🇵🇹",
};

const ACTIVE_LANGUAGES_FALLBACK: LangInfo[] = [
  { code: "ro", flag: "🇷🇴" },
  { code: "en", flag: "🇬🇧" },
  { code: "bg", flag: "🇧🇬" },
];

/**
 * Legacy feature flag key → display label map.
 *
 * Used by `normalizeFeatures` to convert old-style `{ key: true }` features
 * objects into the new `FeatureItem[]` shape. D21 STRICTER preserves this
 * map verbatim from legacy even though new plans should ship as
 * `FeatureItem[]` directly — older DB rows may still carry the bool format.
 */
const LEGACY_LABELS: Record<string, string> = {
  listing: "Listare in portal",
  listing_free: "Listare gratuită",
  listing_full: "Listare completă",
  name_visible: "Nume afișat",
  specialty_visible: "Specialitate afișată",
  facility_visible: "Localitate afișată",
  seo: "SEO optimizat",
  gallery: "Galerie foto nelimitată",
  branded_page: "Pagină branded",
  booking: "Programări online",
  ai_notes: "Note SOAP cu AI",
  messaging: "Mesagerie",
  analytics: "Analytics",
  reminders: "Remindere multi-canal",
  video: "Video consultații",
  benchmark: "Benchmark dashboard",
  referral: "Rețea referral",
  cross_border: "Cross-border",
  api: "API access",
  calendar: "Calendar centralizat",
  admin_dashboard: "Dashboard admin",
  multi_location: "Multi-locație",
  pms_integration: "Integrare PMS",
  landing_page: "Landing page SEO",
};

/**
 * Get display name for a feature — always show RO fallback to EN.
 * Used in feature list placeholder text.
 */
function featureDisplayName(name: string | LocalizedString): string {
  if (typeof name === "string") return name;
  return name.ro || name.en || Object.values(name)[0] || "";
}

/** Get name for a specific lang — returns "" if not present. */
function featureLangName(name: string | LocalizedString, lang: string): string {
  if (typeof name === "string") return lang === "ro" ? name : "";
  return name[lang] || "";
}

/** Set name for a specific lang — promotes string to object on first edit. */
function setFeatureLangName(
  name: string | LocalizedString,
  lang: string,
  value: string,
): LocalizedString {
  const obj: LocalizedString = typeof name === "string" ? { ro: name } : { ...name };
  obj[lang] = value;
  return obj;
}

/**
 * Normalize features payload from API.
 * - Already an array → return as-is (typed as FeatureItem[])
 * - Old `{ key: true }` object → map to FeatureItem[] using LEGACY_LABELS
 * - Anything else → empty array
 */
function normalizeFeatures(raw: unknown): FeatureItem[] {
  if (Array.isArray(raw)) return raw as FeatureItem[];
  if (raw && typeof raw === "object") {
    return Object.entries(raw as Record<string, boolean>)
      .filter(([, v]) => v)
      .map(([k], i) => ({ name: LEGACY_LABELS[k] || k, status: "active" as const, order: i }));
  }
  return [];
}

function computeAnnualPrice(
  monthly: number,
  discountType: string,
  discountValue: number,
  manualAnnual: number | null,
): number | null {
  if (monthly === 0) return 0;
  if (discountType === "months") return monthly * (12 - discountValue);
  if (discountType === "percent") return Math.round(monthly * 12 * (1 - discountValue / 100));
  if (discountType === "manual") return manualAnnual;
  return monthly * 10;
}

const TARGET_FILTERS = ["all", "provider", "facility", "business", "user"] as const;
type TargetFilter = (typeof TARGET_FILTERS)[number];

interface PricingSearch {
  filter?: TargetFilter;
  lang?: string;
}

export const Route = createFileRoute("/_authenticated/pricing")({
  component: PricingPage,
  validateSearch: (search: Record<string, unknown>): PricingSearch => ({
    filter: TARGET_FILTERS.includes(search.filter as TargetFilter)
      ? (search.filter as TargetFilter)
      : undefined,
    lang: typeof search.lang === "string" ? search.lang : undefined,
  }),
});

function PricingPage() {
  const { t } = useLingui();
  const search = useSearch({ from: "/_authenticated/pricing" });
  const navigate = useNavigate({ from: "/pricing" });
  const { dbTarget } = useAdminContext();
  const filter: TargetFilter = search.filter ?? "all";

  const [plans, setPlans] = useState<PlanData[]>([]);
  const [editPlan, setEditPlan] = useState<PlanData | null>(null);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translateStatus, setTranslateStatus] = useState("");
  const [editLang, setEditLang] = useState<string>(search.lang ?? "ro");
  const [langs, setLangs] = useState<LangInfo[]>(ACTIVE_LANGUAGES_FALLBACK);
  const [fieldTranslating, setFieldTranslating] = useState<string | null>(null);

  // Fetch plans whenever the DB target changes — admin may toggle Local↔Prod
  // from the header and expects the table to refresh.
  const plansQuery = useQuery({
    queryKey: ["admin", "pricing", dbTarget],
    queryFn: () => apiFetch.get<PlanData[]>("/api/admin/pricing").catch(() => [] as PlanData[]),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (plansQuery.data) setPlans(plansQuery.data);
  }, [plansQuery.data]);

  // Active languages — `active_languages` lives in the admin settings table,
  // so refetch when the header DB toggle flips (interceptor sets X-Admin-DB).
  useEffect(() => {
    getActiveLangs().then((langCodes) => {
      setLangs(
        langCodes.map((code) => ({
          code,
          flag: LANG_FLAGS[code] || "🌐",
        })),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dbTarget drives the refetch
  }, [dbTarget]);

  /**
   * Translate a single LocalizedString field inline (cheap: 1 API call).
   * Uses `langs` state (already fetched from active languages config).
   */
  const translateField = async (
    fieldName: string,
    _currentValue: Record<string, string>,
    onUpdate: (translated: Record<string, string>) => void,
  ) => {
    setFieldTranslating(fieldName);
    const targetLangs = langs.map((l) => l.code).filter((c) => c !== "ro");

    try {
      const data = await apiFetch.post<{ updates?: Record<string, Record<string, string>> }>(
        "/api/admin/translate",
        {
          entity: "pricingPlan",
          id: editPlan?.id,
          fields: [fieldName],
          targetLangs,
          sourceLang: "ro",
        },
      );
      if (data.updates?.[fieldName]) {
        onUpdate(data.updates[fieldName]);
      }
    } catch {
      /* ignore */
    }
    setFieldTranslating(null);
  };

  const filtered = useMemo(
    () => plans.filter((p) => filter === "all" || p.targetType === filter),
    [plans, filter],
  );

  const handleSave = async () => {
    if (!editPlan) return;
    setSaving(true);

    const annual = computeAnnualPrice(
      editPlan.priceMonthly,
      editPlan.annualDiscountType,
      editPlan.annualDiscountValue,
      editPlan.priceAnnual,
    );

    const payload = {
      id: editPlan.id,
      name: editPlan.name,
      description: editPlan.description,
      priceMonthly: editPlan.priceMonthly,
      priceAnnual: annual,
      annualDiscountType: editPlan.annualDiscountType,
      annualDiscountValue: editPlan.annualDiscountValue,
      bookingsPerMonth: editPlan.bookingsPerMonth,
      providersIncluded: editPlan.providersIncluded,
      aiNotesPerMonth: editPlan.aiNotesPerMonth,
      smsPerMonth: editPlan.smsPerMonth,
      pushPerMonth: editPlan.pushPerMonth,
      viberPerMonth: editPlan.viberPerMonth,
      whatsappPerMonth: editPlan.whatsappPerMonth,
      extraSmsPrice: editPlan.extraSmsPrice,
      byopAllowed: editPlan.byopAllowed,
      extraBookingsPrice: editPlan.extraBookingsPrice,
      extraBookingsAmount: editPlan.extraBookingsAmount,
      extraProviderPrice: editPlan.extraProviderPrice,
      volumeDiscountTiers: editPlan.volumeDiscountTiers,
      transactionFeePct: editPlan.transactionFeePct,
      features: editPlan.features,
      isPopular: editPlan.isPopular,
      isActive: editPlan.isActive,
      badge: editPlan.badge,
      visibility: editPlan.visibility,
    };

    try {
      const updated = await apiFetch.put<PlanData>("/api/admin/pricing", payload);
      setPlans((prev) =>
        prev.map((p) =>
          p.id === updated.id
            ? {
                ...p,
                ...payload,
                priceAnnual: annual,
                version: updated.version ?? p.version,
              }
            : p,
        ),
      );
      setEditPlan(null);
    } catch {
      /* ignore */
    }
    setSaving(false);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">
          <Trans>Pricing plans</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>{plans.length} plans configured</Trans>
        </p>
      </div>

      {/* Filter pills */}
      <div className="mb-4 flex flex-wrap gap-2">
        {TARGET_FILTERS.map((f) => (
          <button
            key={f}
            onClick={() =>
              navigate({
                search: {
                  filter: f === "all" ? undefined : f,
                  lang: editLang !== "ro" ? editLang : undefined,
                },
              })
            }
            className={`rounded-full px-4 py-1.5 text-xs font-medium transition-all ${
              filter === f
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
          >
            {f === "all" ? (
              <Trans>All</Trans>
            ) : f === "provider" ? (
              <Trans>Providers</Trans>
            ) : f === "facility" ? (
              <Trans>Facilities</Trans>
            ) : f === "business" ? (
              <Trans>Business</Trans>
            ) : (
              <Trans>Users</Trans>
            )}
            <span className="ml-1 opacity-60">
              ({f === "all" ? plans.length : plans.filter((p) => p.targetType === f).length})
            </span>
          </button>
        ))}
      </div>

      {/* Plans table */}
      <div className="border-border/50 overflow-hidden rounded-xl border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-border/50 bg-muted/30 border-b">
              <th className="px-4 py-3 text-left text-xs font-semibold">
                <Trans>Plan</Trans>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold">
                <Trans>Type</Trans>
              </th>
              <th className="hidden px-4 py-3 text-right text-xs font-semibold sm:table-cell">
                <Trans>Monthly</Trans>
              </th>
              <th className="hidden px-4 py-3 text-right text-xs font-semibold sm:table-cell">
                <Trans>Annual</Trans>
              </th>
              <th className="hidden px-4 py-3 text-center text-xs font-semibold md:table-cell">
                <Trans>Discount</Trans>
              </th>
              <th className="hidden px-4 py-3 text-center text-xs font-semibold md:table-cell">
                <Trans>Bookings</Trans>
              </th>
              <th className="hidden px-4 py-3 text-center text-xs font-semibold lg:table-cell">
                <Trans>Providers</Trans>
              </th>
              <th className="hidden px-4 py-3 text-center text-xs font-semibold lg:table-cell">
                <Trans>Extra</Trans>
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold">
                <Trans>Active</Trans>
              </th>
              <th className="text-muted-foreground hidden px-4 py-3 text-center text-xs font-semibold lg:table-cell">
                v
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold"></th>
              <th className="w-8 md:hidden"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((plan) => {
              const annualMonthly = plan.priceAnnual ? Math.round(plan.priceAnnual / 12) : null;
              return (
                <ExpandableRow
                  key={plan.id}
                  className="border-border/50 hover:bg-muted/20 border-b last:border-0"
                  mobileDetails={[
                    {
                      label: t`Monthly`,
                      value: plan.priceMonthly === 0 ? t`Free` : `€${plan.priceMonthly}`,
                    },
                    {
                      label: t`Annual`,
                      value: plan.priceAnnual
                        ? `€${annualMonthly}/${t`mo`} (€${plan.priceAnnual}/${t`yr`})`
                        : "—",
                    },
                    {
                      label: t`Bookings`,
                      value: plan.bookingsPerMonth > 0 ? plan.bookingsPerMonth : "—",
                    },
                    {
                      label: t`Providers`,
                      value: plan.providersIncluded > 0 ? plan.providersIncluded : "—",
                    },
                    {
                      label: t`Extra`,
                      value: plan.extraProviderPrice
                        ? `€${plan.extraProviderPrice}/dr (${
                            (plan.volumeDiscountTiers as VolumeTier[] | null)?.length ?? 0
                          } tiers)`
                        : plan.extraBookingsPrice
                          ? `+€${plan.extraBookingsPrice}/${plan.extraBookingsAmount} prg`
                          : "—",
                    },
                    {
                      label: t`Discount`,
                      value:
                        plan.priceMonthly > 0 && plan.priceAnnual
                          ? plan.annualDiscountType === "months"
                            ? `${plan.annualDiscountValue} mo`
                            : plan.annualDiscountType === "percent"
                              ? `${plan.annualDiscountValue}%`
                              : "manual"
                          : "—",
                    },
                    { label: t`Version`, value: `v${plan.version}` },
                  ]}
                >
                  <td className="px-4 py-3">
                    <div className="font-semibold">{plan.name.ro || plan.name.en}</div>
                    <div className="text-muted-foreground text-[0.625rem]">{plan.code}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="secondary" className="text-[0.625rem]">
                      {plan.targetType}
                    </Badge>
                  </td>
                  <td className="hidden px-4 py-3 text-right font-semibold sm:table-cell">
                    {plan.priceMonthly === 0 ? <Trans>Free</Trans> : `€${plan.priceMonthly}`}
                  </td>
                  <td className="hidden px-4 py-3 text-right sm:table-cell">
                    {plan.priceAnnual ? (
                      <div>
                        <div className="font-semibold">
                          €{annualMonthly}/<Trans>mo</Trans>
                        </div>
                        <div className="text-muted-foreground text-[0.625rem]">
                          €{plan.priceAnnual}/<Trans>yr</Trans>
                        </div>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="hidden px-4 py-3 text-center md:table-cell">
                    {plan.priceMonthly > 0 && plan.priceAnnual ? (
                      <span className="text-xs">
                        {plan.annualDiscountType === "months"
                          ? `${plan.annualDiscountValue} mo`
                          : plan.annualDiscountType === "percent"
                            ? `${plan.annualDiscountValue}%`
                            : "manual"}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="hidden px-4 py-3 text-center md:table-cell">
                    {plan.bookingsPerMonth > 0 ? plan.bookingsPerMonth : "—"}
                  </td>
                  <td className="hidden px-4 py-3 text-center lg:table-cell">
                    {plan.providersIncluded > 0 ? plan.providersIncluded : "—"}
                  </td>
                  <td className="hidden px-4 py-3 text-center text-[0.6875rem] lg:table-cell">
                    {plan.extraProviderPrice ? (
                      <span>
                        €{plan.extraProviderPrice}/dr
                        {(plan.volumeDiscountTiers as VolumeTier[] | null)?.length
                          ? ` (${(plan.volumeDiscountTiers as VolumeTier[] | null)?.length} tiers)`
                          : ""}
                      </span>
                    ) : plan.extraBookingsPrice ? (
                      <span>
                        +€{plan.extraBookingsPrice}/{plan.extraBookingsAmount} prg
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex flex-col items-center gap-0.5">
                      <span
                        className={`inline-block h-2 w-2 rounded-full ${
                          plan.isActive ? "bg-emerald-500" : "bg-zinc-300"
                        }`}
                      />
                      {plan.visibility === "coming_soon" && (
                        <span className="rounded-full bg-amber-100 px-1.5 py-0 text-[0.5625rem] font-semibold text-amber-800 dark:bg-amber-950 dark:text-amber-200">
                          Soon
                        </span>
                      )}
                      {plan.visibility === "draft" && (
                        <span className="rounded-full bg-zinc-200 px-1.5 py-0 text-[0.5625rem] font-semibold text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                          Draft
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="text-muted-foreground hidden px-4 py-3 text-center text-[0.625rem] lg:table-cell">
                    v{plan.version}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditPlan({ ...plan, features: normalizeFeatures(plan.features) });
                      }}
                    >
                      <Trans>Edit</Trans>
                    </Button>
                  </td>
                </ExpandableRow>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit Dialog */}
      <Dialog
        open={editPlan !== null}
        onOpenChange={(open) => {
          if (!open) setEditPlan(null);
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          {editPlan && (
            <>
              <DialogHeader>
                <DialogTitle>
                  <Trans>
                    Edit: {editPlan.name.ro || editPlan.name.en} ({editPlan.code})
                  </Trans>
                </DialogTitle>
              </DialogHeader>

              <div className="mt-4 space-y-6">
                {/* Language tabs */}
                <div className="border-border/50 flex w-fit gap-1 rounded-lg border p-0.5">
                  {langs.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => setEditLang(lang.code)}
                      className={`rounded-md px-3 py-1 text-xs font-medium transition-all ${
                        editLang === lang.code
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {lang.flag} {lang.code.toUpperCase()}
                    </button>
                  ))}
                </div>

                {/* Plan name & description (per language) */}
                <div>
                  <h3 className="mb-3 text-sm font-bold">
                    <Trans>Name and description ({editLang.toUpperCase()})</Trans>
                  </h3>
                  <div className="grid gap-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium">
                        <Trans>Plan name</Trans>
                      </label>
                      <div className="flex gap-1">
                        <Input
                          className="flex-1"
                          value={editPlan.name[editLang] || ""}
                          onChange={(e) =>
                            setEditPlan({
                              ...editPlan,
                              name: { ...editPlan.name, [editLang]: e.target.value },
                            })
                          }
                          placeholder={`${t`Plan name`} (${editLang.toUpperCase()})`}
                        />
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-primary shrink-0 px-1.5 text-xs disabled:opacity-30"
                          title={t`Translate name from RO`}
                          disabled={fieldTranslating === "name"}
                          onClick={() =>
                            translateField("name", editPlan.name, (translated) =>
                              setEditPlan({ ...editPlan, name: translated }),
                            )
                          }
                        >
                          {fieldTranslating === "name" ? "⏳" : "🤖"}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium">
                        <Trans>Description</Trans>
                      </label>
                      <div className="flex gap-1">
                        <Input
                          className="flex-1"
                          value={editPlan.description[editLang] || ""}
                          onChange={(e) =>
                            setEditPlan({
                              ...editPlan,
                              description: {
                                ...editPlan.description,
                                [editLang]: e.target.value,
                              },
                            })
                          }
                          placeholder={`${t`Description`} (${editLang.toUpperCase()})`}
                        />
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-primary shrink-0 px-1.5 text-xs disabled:opacity-30"
                          title={t`Translate description from RO`}
                          disabled={fieldTranslating === "description"}
                          onClick={() =>
                            translateField("description", editPlan.description, (translated) =>
                              setEditPlan({ ...editPlan, description: translated }),
                            )
                          }
                        >
                          {fieldTranslating === "description" ? "⏳" : "🤖"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pricing section */}
                <div>
                  <h3 className="mb-3 text-sm font-bold">
                    <Trans>Pricing</Trans>
                  </h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium">
                        <Trans>Monthly price (€)</Trans>
                      </label>
                      <Input
                        type="number"
                        value={editPlan.priceMonthly}
                        onChange={(e) =>
                          setEditPlan({ ...editPlan, priceMonthly: Number(e.target.value) })
                        }
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium">
                        <Trans>Currency</Trans>
                      </label>
                      <Input value={editPlan.currency} disabled />
                    </div>
                  </div>

                  {/* Annual discount */}
                  <div className="mt-4">
                    <label className="mb-2 block text-xs font-bold">
                      <Trans>Annual discount</Trans>
                    </label>
                    <div className="flex items-center gap-2">
                      {(["months", "percent", "manual"] as const).map((type) => (
                        <button
                          key={type}
                          onClick={() => setEditPlan({ ...editPlan, annualDiscountType: type })}
                          className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                            editPlan.annualDiscountType === type
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {type === "months" ? (
                            <Trans>Free months</Trans>
                          ) : type === "percent" ? (
                            <Trans>Percent %</Trans>
                          ) : (
                            <Trans>Manual price</Trans>
                          )}
                        </button>
                      ))}
                    </div>

                    <div className="mt-3 grid gap-4 sm:grid-cols-2">
                      {editPlan.annualDiscountType === "months" && (
                        <div>
                          <label className="mb-1 block text-xs font-medium">
                            <Trans>Number of free months</Trans>
                          </label>
                          <Input
                            type="number"
                            min="0"
                            max="6"
                            value={editPlan.annualDiscountValue}
                            onChange={(e) =>
                              setEditPlan({
                                ...editPlan,
                                annualDiscountValue: Number(e.target.value),
                              })
                            }
                          />
                          <p className="text-muted-foreground mt-1 text-[0.625rem]">
                            <Trans>
                              Annual price: €
                              {editPlan.priceMonthly * (12 - editPlan.annualDiscountValue)} (
                              {12 - editPlan.annualDiscountValue} months × €{editPlan.priceMonthly})
                            </Trans>
                          </p>
                        </div>
                      )}

                      {editPlan.annualDiscountType === "percent" && (
                        <div>
                          <label className="mb-1 block text-xs font-medium">
                            <Trans>Discount percent (%)</Trans>
                          </label>
                          <Input
                            type="number"
                            min="0"
                            max="50"
                            value={editPlan.annualDiscountValue}
                            onChange={(e) =>
                              setEditPlan({
                                ...editPlan,
                                annualDiscountValue: Number(e.target.value),
                              })
                            }
                          />
                          <p className="text-muted-foreground mt-1 text-[0.625rem]">
                            <Trans>
                              Annual price: €
                              {Math.round(
                                editPlan.priceMonthly *
                                  12 *
                                  (1 - editPlan.annualDiscountValue / 100),
                              )}{" "}
                              ({editPlan.annualDiscountValue}% off €{editPlan.priceMonthly * 12})
                            </Trans>
                          </p>
                        </div>
                      )}

                      {editPlan.annualDiscountType === "manual" && (
                        <div>
                          <label className="mb-1 block text-xs font-medium">
                            <Trans>Annual total (€)</Trans>
                          </label>
                          <Input
                            type="number"
                            value={editPlan.priceAnnual ?? editPlan.priceMonthly * 10}
                            onChange={(e) =>
                              setEditPlan({ ...editPlan, priceAnnual: Number(e.target.value) })
                            }
                          />
                          <p className="text-muted-foreground mt-1 text-[0.625rem]">
                            <Trans>
                              Equivalent: €
                              {Math.round(
                                (editPlan.priceAnnual ?? editPlan.priceMonthly * 10) / 12,
                              )}
                              /month
                            </Trans>
                          </p>
                        </div>
                      )}

                      <div>
                        <label className="mb-1 block text-xs font-medium">
                          <Trans>Transaction fee (%)</Trans>
                        </label>
                        <Input
                          type="number"
                          step="0.5"
                          min="0"
                          max="10"
                          value={editPlan.transactionFeePct}
                          onChange={(e) =>
                            setEditPlan({
                              ...editPlan,
                              transactionFeePct: Number(e.target.value),
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Limits */}
                <div>
                  <h3 className="mb-3 text-sm font-bold">
                    <Trans>Limits</Trans>
                  </h3>
                  <p className="text-muted-foreground mb-3 text-[0.625rem]">
                    <Trans>-1 = unlimited, 0 = disabled</Trans>
                  </p>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium">
                        <Trans>Bookings/month</Trans>
                      </label>
                      <Input
                        type="number"
                        min="-1"
                        value={editPlan.bookingsPerMonth}
                        onChange={(e) =>
                          setEditPlan({ ...editPlan, bookingsPerMonth: Number(e.target.value) })
                        }
                      />
                      <p className="text-muted-foreground mt-0.5 text-[0.6rem]">
                        {editPlan.bookingsPerMonth === -1 ? (
                          <Trans>Unlimited</Trans>
                        ) : editPlan.bookingsPerMonth === 0 ? (
                          <Trans>No bookings</Trans>
                        ) : (
                          <Trans>{editPlan.bookingsPerMonth}/month</Trans>
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium">
                        <Trans>AI notes/month</Trans>
                      </label>
                      <Input
                        type="number"
                        min="-1"
                        value={editPlan.aiNotesPerMonth}
                        onChange={(e) =>
                          setEditPlan({ ...editPlan, aiNotesPerMonth: Number(e.target.value) })
                        }
                      />
                      <p className="text-muted-foreground mt-0.5 text-[0.6rem]">
                        {editPlan.aiNotesPerMonth === -1 ? (
                          <Trans>Unlimited</Trans>
                        ) : editPlan.aiNotesPerMonth === 0 ? (
                          <Trans>Disabled</Trans>
                        ) : (
                          <Trans>{editPlan.aiNotesPerMonth}/month</Trans>
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium">
                        <Trans>Providers included</Trans>
                      </label>
                      <Input
                        type="number"
                        min="0"
                        value={editPlan.providersIncluded}
                        onChange={(e) =>
                          setEditPlan({ ...editPlan, providersIncluded: Number(e.target.value) })
                        }
                      />
                    </div>
                  </div>

                  {/* Extra bookings */}
                  {editPlan.bookingsPerMonth > 0 && (
                    <div className="border-border/30 mt-3 grid gap-4 border-t pt-3 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-xs font-medium">
                          <Trans>Extra bookings: price (€)</Trans>
                        </label>
                        <Input
                          type="number"
                          value={editPlan.extraBookingsPrice ?? ""}
                          onChange={(e) =>
                            setEditPlan({
                              ...editPlan,
                              extraBookingsPrice: e.target.value ? Number(e.target.value) : null,
                            })
                          }
                          placeholder={t`e.g. 19`}
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium">
                          <Trans>Extra bookings: amount</Trans>
                        </label>
                        <Input
                          type="number"
                          value={editPlan.extraBookingsAmount}
                          onChange={(e) =>
                            setEditPlan({
                              ...editPlan,
                              extraBookingsAmount: Number(e.target.value),
                            })
                          }
                          placeholder={t`e.g. 50`}
                        />
                      </div>
                      {editPlan.extraBookingsPrice ? (
                        <p className="text-muted-foreground col-span-2 text-[0.625rem]">
                          <Trans>
                            +€{editPlan.extraBookingsPrice} per {editPlan.extraBookingsAmount} extra
                            bookings
                          </Trans>
                        </p>
                      ) : null}
                    </div>
                  )}
                </div>

                {/* Messaging */}
                <div>
                  <h3 className="mb-3 text-sm font-bold">
                    <Trans>Messaging</Trans>
                  </h3>
                  <p className="text-muted-foreground mb-3 text-[0.625rem]">
                    <Trans>Per-channel limits per month. -1 = unlimited, 0 = disabled.</Trans>
                  </p>
                  <div className="grid gap-4 sm:grid-cols-4">
                    <div>
                      <label className="mb-1 block text-xs font-medium">SMS</label>
                      <Input
                        type="number"
                        min="-1"
                        value={editPlan.smsPerMonth}
                        onChange={(e) =>
                          setEditPlan({ ...editPlan, smsPerMonth: Number(e.target.value) })
                        }
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium">Push</label>
                      <Input
                        type="number"
                        min="-1"
                        value={editPlan.pushPerMonth}
                        onChange={(e) =>
                          setEditPlan({ ...editPlan, pushPerMonth: Number(e.target.value) })
                        }
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium">Viber</label>
                      <Input
                        type="number"
                        min="-1"
                        value={editPlan.viberPerMonth}
                        onChange={(e) =>
                          setEditPlan({ ...editPlan, viberPerMonth: Number(e.target.value) })
                        }
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium">WhatsApp</label>
                      <Input
                        type="number"
                        min="-1"
                        value={editPlan.whatsappPerMonth}
                        onChange={(e) =>
                          setEditPlan({ ...editPlan, whatsappPerMonth: Number(e.target.value) })
                        }
                      />
                    </div>
                  </div>
                  <div className="mt-3 grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium">
                        <Trans>Extra SMS price (€)</Trans>
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editPlan.extraSmsPrice ?? ""}
                        onChange={(e) =>
                          setEditPlan({
                            ...editPlan,
                            extraSmsPrice: e.target.value ? Number(e.target.value) : null,
                          })
                        }
                        placeholder={t`e.g. 0.03`}
                      />
                    </div>
                    <div className="flex items-end pb-1">
                      <label className="flex items-center gap-2 text-xs font-medium">
                        <input
                          type="checkbox"
                          checked={editPlan.byopAllowed}
                          onChange={(e) =>
                            setEditPlan({ ...editPlan, byopAllowed: e.target.checked })
                          }
                          className="accent-primary h-4 w-4 rounded"
                        />
                        <Trans>BYOP (bring own SMS provider)</Trans>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Volume discount tiers — provider plans only, conditional on paid */}
                {editPlan.targetType === "provider" && editPlan.priceMonthly > 0 && (
                  <div>
                    <h3 className="mb-3 text-sm font-bold">
                      <Trans>Additional providers (clinics)</Trans>
                    </h3>
                    <p className="text-muted-foreground mb-3 text-xs">
                      <Trans>
                        Per-provider price is set from the volume discount tier table below.
                      </Trans>
                    </p>

                    {/* Tiers table */}
                    <div className="border-border/50 overflow-hidden rounded-lg border">
                      <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-0 text-xs">
                        {/* Header */}
                        <div className="bg-muted/30 px-3 py-2 font-medium">
                          <Trans>From</Trans>
                        </div>
                        <div className="bg-muted/30 px-3 py-2 font-medium">
                          <Trans>To</Trans>
                        </div>
                        <div className="bg-muted/30 px-3 py-2 text-right font-medium">
                          <Trans>€/provider</Trans>
                        </div>
                        <div className="bg-muted/30 px-3 py-2 text-right font-medium">
                          <Trans>Discount %</Trans>
                        </div>
                        <div className="bg-muted/30 w-9 px-2 py-2"></div>

                        {/* Rows */}
                        {(editPlan.volumeDiscountTiers ?? []).map((tier, idx) => (
                          <div
                            key={idx}
                            className="border-border/30 col-span-5 grid grid-cols-subgrid border-t"
                          >
                            <div className="px-3 py-1.5">
                              <Input
                                type="number"
                                min="1"
                                className="h-8 text-xs"
                                value={tier.minProviders}
                                onChange={(e) => {
                                  const tiers = [...(editPlan.volumeDiscountTiers ?? [])];
                                  tiers[idx] = {
                                    ...tier,
                                    minProviders: Number(e.target.value),
                                  };
                                  setEditPlan({ ...editPlan, volumeDiscountTiers: tiers });
                                }}
                              />
                            </div>
                            <div className="px-3 py-1.5">
                              <Input
                                type="number"
                                min="1"
                                className="h-8 text-xs"
                                value={tier.maxProviders}
                                onChange={(e) => {
                                  const tiers = [...(editPlan.volumeDiscountTiers ?? [])];
                                  tiers[idx] = {
                                    ...tier,
                                    maxProviders: Number(e.target.value),
                                  };
                                  setEditPlan({ ...editPlan, volumeDiscountTiers: tiers });
                                }}
                              />
                            </div>
                            <div className="px-3 py-1.5">
                              <Input
                                type="number"
                                className="h-8 text-right text-xs"
                                value={tier.pricePerProvider}
                                onChange={(e) => {
                                  const price = Number(e.target.value);
                                  const base = editPlan.priceMonthly;
                                  const disc = base > 0 ? Math.round((1 - price / base) * 100) : 0;
                                  const tiers = [...(editPlan.volumeDiscountTiers ?? [])];
                                  tiers[idx] = {
                                    ...tier,
                                    pricePerProvider: price,
                                    discountPct: Math.max(0, disc),
                                  };
                                  setEditPlan({ ...editPlan, volumeDiscountTiers: tiers });
                                }}
                              />
                            </div>
                            <div className="px-3 py-1.5">
                              <Input
                                type="number"
                                min="0"
                                max="90"
                                className="h-8 text-right text-xs"
                                value={tier.discountPct}
                                onChange={(e) => {
                                  const disc = Number(e.target.value);
                                  const base = editPlan.priceMonthly;
                                  const price = Math.round(base * (1 - disc / 100));
                                  const tiers = [...(editPlan.volumeDiscountTiers ?? [])];
                                  tiers[idx] = {
                                    ...tier,
                                    discountPct: disc,
                                    pricePerProvider: price,
                                  };
                                  setEditPlan({ ...editPlan, volumeDiscountTiers: tiers });
                                }}
                              />
                            </div>
                            <div className="flex items-center justify-center px-1 py-1.5">
                              <button
                                type="button"
                                className="text-muted-foreground hover:text-destructive text-sm"
                                onClick={() => {
                                  const tiers = (editPlan.volumeDiscountTiers ?? []).filter(
                                    (_, i) => i !== idx,
                                  );
                                  setEditPlan({ ...editPlan, volumeDiscountTiers: tiers });
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        const tiers = editPlan.volumeDiscountTiers ?? [];
                        const lastMax = tiers.length > 0 ? tiers[tiers.length - 1].maxProviders : 1;
                        const base = editPlan.priceMonthly;
                        setEditPlan({
                          ...editPlan,
                          volumeDiscountTiers: [
                            ...tiers,
                            {
                              minProviders: lastMax + 1,
                              maxProviders: lastMax + 50,
                              pricePerProvider: base,
                              discountPct: 0,
                            },
                          ],
                        });
                      }}
                    >
                      <Trans>+ Add tier</Trans>
                    </Button>

                    {/* Cost preview */}
                    {(editPlan.volumeDiscountTiers ?? []).length > 0 && (
                      <div className="bg-muted/30 mt-3 rounded-lg p-3">
                        <p className="mb-1.5 text-[0.625rem] font-bold">
                          <Trans>Clinic cost preview:</Trans>
                        </p>
                        {[5, 15, 50].map((n) => {
                          const base = editPlan.priceMonthly;
                          const tiers = editPlan.volumeDiscountTiers ?? [];
                          let total = base; // 1 provider included in base plan
                          for (let doc = 2; doc <= n; doc++) {
                            const tier = tiers.find(
                              (tt) => doc >= tt.minProviders && doc <= tt.maxProviders,
                            );
                            total += tier ? tier.pricePerProvider : base;
                          }
                          const individual = n * base;
                          const savingsPct =
                            individual > 0
                              ? Math.round(((individual - total) / individual) * 100)
                              : 0;
                          return (
                            <p key={n} className="text-muted-foreground text-[0.625rem]">
                              <Trans>
                                {n} providers: €{total}/month (vs €{individual} individual ={" "}
                                {savingsPct > 0 ? `-${savingsPct}%` : "0%"})
                              </Trans>
                            </p>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Features */}
                <div>
                  <h3 className="mb-3 text-sm font-bold">
                    <Trans>Features</Trans>
                  </h3>
                  <div className="space-y-2">
                    {[...editPlan.features]
                      .sort((a, b) => a.order - b.order)
                      .map((feat, idx) => (
                        <div
                          key={idx}
                          className="border-border/50 flex items-center gap-2 rounded-lg border px-3 py-2"
                        >
                          {/* Up/Down buttons */}
                          <div className="flex flex-col gap-0.5">
                            <button
                              type="button"
                              className="text-muted-foreground hover:text-foreground text-xs leading-none disabled:opacity-30"
                              disabled={idx === 0}
                              onClick={() => {
                                const sorted = [...editPlan.features].sort(
                                  (a, b) => a.order - b.order,
                                );
                                const prev = sorted[idx - 1];
                                const curr = sorted[idx];
                                const newFeatures = editPlan.features.map((f) => {
                                  if (f === curr) return { ...f, order: prev.order };
                                  if (f === prev) return { ...f, order: curr.order };
                                  return f;
                                });
                                setEditPlan({ ...editPlan, features: newFeatures });
                              }}
                            >
                              ▲
                            </button>
                            <button
                              type="button"
                              className="text-muted-foreground hover:text-foreground text-xs leading-none disabled:opacity-30"
                              disabled={idx === editPlan.features.length - 1}
                              onClick={() => {
                                const sorted = [...editPlan.features].sort(
                                  (a, b) => a.order - b.order,
                                );
                                const next = sorted[idx + 1];
                                const curr = sorted[idx];
                                const newFeatures = editPlan.features.map((f) => {
                                  if (f === curr) return { ...f, order: next.order };
                                  if (f === next) return { ...f, order: curr.order };
                                  return f;
                                });
                                setEditPlan({ ...editPlan, features: newFeatures });
                              }}
                            >
                              ▼
                            </button>
                          </div>

                          {/* Feature name (per language) */}
                          <Input
                            className="flex-1 text-xs"
                            value={featureLangName(feat.name, editLang)}
                            placeholder={
                              featureDisplayName(feat.name) ||
                              `${t`Feature`} (${editLang.toUpperCase()})`
                            }
                            onChange={(e) => {
                              const newFeatures = editPlan.features.map((f) =>
                                f === feat
                                  ? {
                                      ...f,
                                      name: setFeatureLangName(f.name, editLang, e.target.value),
                                    }
                                  : f,
                              );
                              setEditPlan({ ...editPlan, features: newFeatures });
                            }}
                          />
                          <button
                            type="button"
                            className="text-muted-foreground hover:text-primary shrink-0 px-0.5 text-xs disabled:opacity-30"
                            title={t`Translate from RO`}
                            disabled={fieldTranslating === `feat-${idx}`}
                            onClick={async () => {
                              setFieldTranslating(`feat-${idx}`);
                              const nameObj: LocalizedString =
                                typeof feat.name === "string"
                                  ? { ro: feat.name }
                                  : { ...(feat.name as LocalizedString) };
                              const targets = langs.map((l) => l.code).filter((c) => c !== "ro");
                              try {
                                const translated = await apiFetch.post<LocalizedString>(
                                  "/api/admin/translate-text",
                                  {
                                    text: nameObj,
                                    targetLangs: targets,
                                    sourceLang: "ro",
                                    context: "subscription plan feature name",
                                  },
                                );
                                const newFeatures = editPlan.features.map((f) =>
                                  f === feat ? { ...f, name: translated } : f,
                                );
                                setEditPlan({ ...editPlan, features: newFeatures });
                              } catch {
                                /* ignore */
                              }
                              setFieldTranslating(null);
                            }}
                          >
                            {fieldTranslating === `feat-${idx}` ? "⏳" : "🤖"}
                          </button>

                          {/* Status select */}
                          <select
                            className="border-border/50 focus:ring-primary/30 focus:border-primary rounded-md border bg-transparent px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-inset"
                            value={feat.status}
                            onChange={(e) => {
                              const newFeatures = editPlan.features.map((f) =>
                                f === feat
                                  ? { ...f, status: e.target.value as FeatureItem["status"] }
                                  : f,
                              );
                              setEditPlan({ ...editPlan, features: newFeatures });
                            }}
                          >
                            <option value="active">Active</option>
                            <option value="new">NEW</option>
                            <option value="soon">SOON</option>
                          </select>

                          {/* Delete button */}
                          <button
                            type="button"
                            className="text-muted-foreground hover:text-destructive text-sm"
                            onClick={() => {
                              const newFeatures = editPlan.features.filter((f) => f !== feat);
                              setEditPlan({ ...editPlan, features: newFeatures });
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}

                    {/* Add feature button */}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2 w-full"
                      onClick={() => {
                        const maxOrder = editPlan.features.reduce(
                          (max, f) => Math.max(max, f.order),
                          -1,
                        );
                        setEditPlan({
                          ...editPlan,
                          features: [
                            ...editPlan.features,
                            {
                              name: { ro: "", en: "", bg: "" },
                              status: "active" as const,
                              order: maxOrder + 1,
                            },
                          ],
                        });
                      }}
                    >
                      <Trans>+ Add feature</Trans>
                    </Button>
                  </div>
                </div>

                {/* Display */}
                <div>
                  <h3 className="mb-3 text-sm font-bold">
                    <Trans>Display</Trans>
                  </h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs">
                        <Trans>Popular highlight</Trans>
                      </span>
                      <Switch
                        checked={editPlan.isPopular}
                        onCheckedChange={(checked) =>
                          setEditPlan({ ...editPlan, isPopular: checked })
                        }
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs">
                        <Trans>Active</Trans>
                      </span>
                      <Switch
                        checked={editPlan.isActive}
                        onCheckedChange={(checked) =>
                          setEditPlan({ ...editPlan, isActive: checked })
                        }
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium">
                        <Trans>Badge text</Trans>
                      </label>
                      <Input
                        value={editPlan.badge ?? ""}
                        onChange={(e) =>
                          setEditPlan({ ...editPlan, badge: e.target.value || null })
                        }
                        placeholder={t`e.g. Recommended, Soon`}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-2 block text-xs font-bold">
                        <Trans>Visibility</Trans>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { v: "active" as const, label: t`Active (purchasable)` },
                          { v: "coming_soon" as const, label: t`Coming soon (waitlist)` },
                          { v: "draft" as const, label: t`Draft (admin-only)` },
                        ].map(({ v, label }) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => setEditPlan({ ...editPlan, visibility: v })}
                            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                              editPlan.visibility === v
                                ? v === "coming_soon"
                                  ? "bg-amber-500 text-white"
                                  : v === "draft"
                                    ? "bg-zinc-600 text-white"
                                    : "bg-primary text-primary-foreground"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      {editPlan.visibility === "coming_soon" && (
                        <p className="text-muted-foreground mt-2 text-[0.6875rem] leading-relaxed">
                          <Trans>
                            Plan appears on landing with waitlist signup CTA. When you flip it to
                            "Active", everyone in waitlist will be auto-emailed.
                          </Trans>
                        </p>
                      )}
                      {editPlan.visibility === "draft" && (
                        <p className="text-muted-foreground mt-2 text-[0.6875rem] leading-relaxed">
                          <Trans>
                            Plan does not show on public landing. Visible only in admin.
                          </Trans>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => setEditPlan(null)}>
                  <Trans>Cancel</Trans>
                </Button>
                <Button
                  variant="outline"
                  disabled={translating}
                  onClick={async () => {
                    setTranslating(true);
                    setTranslateStatus(t`Translating from RO...`);
                    const targetLangs = langs.map((l) => l.code).filter((c) => c !== "ro");
                    try {
                      const data = await apiFetch.post<{
                        updates?: {
                          name?: Record<string, string>;
                          description?: Record<string, string>;
                          features?: FeatureItem[];
                        };
                      }>("/api/admin/translate", {
                        entity: "pricingPlan",
                        id: editPlan.id,
                        fields: ["name", "description", "features"],
                        targetLangs,
                        sourceLang: "ro",
                      });
                      const updatedPlan = {
                        ...editPlan,
                        ...(data.updates?.name && { name: data.updates.name }),
                        ...(data.updates?.description && { description: data.updates.description }),
                        ...(data.updates?.features && { features: data.updates.features }),
                      };
                      setEditPlan(updatedPlan);
                      setPlans((prev) =>
                        prev.map((p) => (p.id === editPlan.id ? { ...p, ...updatedPlan } : p)),
                      );
                      setTranslateStatus(
                        `✅ ${t`Translated into ${targetLangs.length} languages!`}`,
                      );
                    } catch {
                      setTranslateStatus(`❌ ${t`Translation error`}`);
                    }
                    setTranslating(false);
                    setTimeout(() => setTranslateStatus(""), 3000);
                  }}
                >
                  {translating ? <Trans>⏳ Translating...</Trans> : <Trans>🤖 Translate AI</Trans>}
                </Button>
                {translateStatus && <span className="text-xs">{translateStatus}</span>}
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? <Trans>Saving...</Trans> : <Trans>Save changes</Trans>}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
